/**
 * iter-120 round-4 follow-up #8 — SSO callback page.
 *
 * The backend's `/api/auth/sso/{provider}/callback` redirects the user
 * here with either:
 *   • `?token=<jwt>` — successful auth → persist + redirect to /dashboard
 *   • `?error=...`   — failed auth → bounce back to /login with the
 *                       error surfaced as a toast.
 */
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth-context";

// iter-120 round-4 follow-up #9 — Translate technical backend error
// codes into human-friendly messages so the user understands what to do.
// Keys are exact lowercased substrings; first match wins.
const SSO_ERROR_MESSAGES = [
    ["user_not_found", "Your email isn’t registered. Ask your admin to invite you first."],
    ["tenant_mismatch", "Your account belongs to a different organisation than this SSO setup."],
    ["account_disabled", "Your account is disabled. Contact your admin to re-enable it."],
    ["password_only_user", "This account is not configured for SSO. Please sign in with email and password instead."],
    ["wrong_sso_provider:microsoft", "Your account is configured for Microsoft SSO. Please use the Microsoft button on the login page."],
    ["wrong_sso_provider:google", "Your account is configured for Google SSO. Please use the Google button on the login page."],
    ["wrong_sso_provider", "Your account is configured for a different SSO provider. Use the matching button on the login page."],
    ["email_not_returned_by_idp", "Your identity provider didn’t return an email address — please retry with a different account."],
    ["id_token_missing", "Your identity provider didn’t return a valid session token. Please try again."],
    ["id_token_decode_failed", "Your identity provider returned an unreadable session token. Please try again."],
    ["token_exchange_invalid_json", "Your identity provider returned an unexpected response. Please try again."],
    ["token_exchange_network_error", "Could not reach your identity provider. Check your network and try again."],
    ["token_exchange_", "Your identity provider rejected the sign-in request. Ask your admin to verify the SSO credentials."],
    ["state_provider_mismatch", "Sign-in state was tampered with. Please start again."],
    ["state_malformed", "Sign-in state was malformed. Please start again."],
    ["sso link expired", "Sign-in took too long and expired. Please try again."],
    ["sso state token invalid", "Sign-in state token is invalid. Please try again."],
    ["missing_code_or_state", "Sign-in was interrupted. Please try again."],
    ["unsupported_provider", "This SSO provider isn’t supported."],
];

function friendlyError(raw) {
    if (!raw) return "Sign-in failed. Please try again.";
    const low = raw.toLowerCase();
    for (const [needle, msg] of SSO_ERROR_MESSAGES) {
        if (low.includes(needle)) return msg;
    }
    // Backend messages that are already human-readable (start with capital,
    // contain spaces) are surfaced verbatim.
    if (/\s/.test(raw) && raw[0] === raw[0].toUpperCase()) return raw;
    return "Sign-in failed. Please try again.";
}

export default function SsoCallback() {
    const [sp] = useSearchParams();
    const navigate = useNavigate();
    const { setUser } = useAuth();
    const [status, setStatus] = useState("processing"); // processing | ok | error
    const handledRef = React.useRef(false);
    useEffect(() => {
        if (handledRef.current) return;
        handledRef.current = true;
        const token = sp.get("token");
        const error = sp.get("error");

        console.log("[SSO-CALLBACK] SsoCallback mounted", { token: token ? `***${token.slice(-20)}` : null, error });

        if (error) {
            console.error("[SSO-CALLBACK] Error from backend:", error);
            setStatus("error");
            const msg = friendlyError(error);
            toast.error(msg);
            const t = setTimeout(() => {
                navigate(`/login?sso_error=${encodeURIComponent(msg)}`, { replace: true });
            }, 1500);
            return () => clearTimeout(t);
        }

        if (!token) {
            console.error("[SSO-CALLBACK] No token in query params");
            setStatus("error");
            toast.error("SSO callback missing session token.");
            const t = setTimeout(() => navigate("/login", { replace: true }), 1200);
            return () => clearTimeout(t);
        }

        // Persist the token, hydrate /auth/me, then bounce to dashboard.
        console.log("[SSO-CALLBACK] Storing token in localStorage");
        localStorage.setItem("analyzio_token", token);

        (async () => {
            try {
                console.log("[SSO-CALLBACK] Calling /auth/me");
                const r = await api.get("/auth/me");
                console.log("[SSO-CALLBACK] /auth/me succeeded", { user_id: r.data?.id, email: r.data?.email });
                localStorage.setItem("analyzio_user", JSON.stringify(r.data));
                setUser(r.data);
                setStatus("ok");
                toast.success(`Welcome, ${r.data.name || r.data.email}!`);
                console.log("[SSO-CALLBACK] Navigating to dashboard");
                navigate("/", { replace: true });
            } catch (e) {
                console.error("[SSO-CALLBACK] /auth/me failed:", e?.response?.status, e?.response?.data?.detail || e.message);
                localStorage.removeItem("analyzio_token");
                localStorage.removeItem("analyzio_user");
                setStatus("error");
                toast.error("Could not finalise SSO sign-in. Please try again.");
                setTimeout(() => navigate("/login", { replace: true }), 1200);
            }
        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="min-h-screen flex items-center justify-center bg-background" data-testid="sso-callback-page">
            <div className="text-center space-y-3">
                <div className="w-10 h-10 mx-auto border-2 border-primary border-t-transparent rounded-full animate-spin" />
                <div className="text-sm text-muted-foreground">
                    {status === "processing" && "Finalising SSO sign-in…"}
                    {status === "ok" && "Signed in — redirecting…"}
                    {status === "error" && "Sign-in failed — returning to login…"}
                </div>
            </div>
        </div>
    );
}
